import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { TextProducer } from '@src/components/textProcessor/TextProducer';
import { findOriginalVideoElementAtCurrentTime } from '@src/utility/elements';
import { useMemo } from 'react';

import type { FloatingToolbarHost } from '@src/components/common/floatingToolbar/FloatingToolbarHost';
import type { CompositionCanvasQuickEditHandle } from '@src/components/content-components/CompositionCanvasActionsLayer';
import type { ClipVideoCreatorAdapter } from '@src/components/content-components/hooks/ClipVideoCreatorAdapter';
import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import type { AspectRatio } from '@src/types.ts/video';
import type { RefObject } from 'react';

export interface ClipPreviewToolbarHostInput {
  adapter: ClipVideoCreatorAdapter;
  editorStore: ClipPreviewEditorStore;
  videoSourceStore: VideoSourceStore;
  /** The preview's renderer container; the warm canvas iframe is sized to it. */
  rendererContainerRef: RefObject<HTMLElement | null>;
  /**
   * The canvas action layer's quick-edit handle: the text bar's Edit opens its
   * popover (the chip's pencil did, before the bar). Without one the bar has
   * no Edit.
   */
  quickEdit?: RefObject<CompositionCanvasQuickEditHandle | null>;
}

/**
 * PLAT-12102 DS1 — the floating toolbars' host in the Content Studio clip
 * preview, built from the clip's own adapter, editor and source (never the
 * Creator Studio store, which `useVideoCreatorStore()` returns here too).
 *
 * Where the adapter would hand back a Creator Studio object, the host builds
 * the clip's own instead:
 * - `textProducer` — the adapter's delegates to Creator Studio's, which writes
 *   through the Creator Studio editor; this one runs on the adapter, whose root
 *   proxy points it at the clip editor and source.
 * - `sceneLayoutIndexStore` — the clip editor store's (the root proxy does not
 *   override that key).
 * - `karaokeProducer` — Creator Studio's, so its default aspect ratio is the
 *   Creator Studio video's; wrapped to default to the clip's.
 *
 * No `settings` (the modal has no panel, so no More) and no caption Style (the
 * player bar's Captions picker has it). Rendered in a layer above the canvas.
 * With no panel, the text bar's Edit opens the canvas layer's quick-edit
 * popover for the text's content (`quickEdit`).
 */
export function createClipPreviewToolbarHost({
  adapter,
  editorStore,
  videoSourceStore,
  rendererContainerRef,
  quickEdit,
}: ClipPreviewToolbarHostInput): FloatingToolbarHost {
  const textProducer = new TextProducer(
    adapter as unknown as VideoCreatorStore,
  );
  const karaokeProducer = {
    getKaraokeTextSettingByAspectRatio: (
      aspectRatio: AspectRatio = adapter.getCurrentAspectRatio(),
    ) =>
      adapter.karaokeProducer.getKaraokeTextSettingByAspectRatio(aspectRatio),
  };

  return {
    get activeElementIds() {
      return adapter.activeElementIds;
    },
    getActiveElement: () => adapter.getActiveElement(),
    selectElement: id => {
      // Push the pending source first, so the renderer knows a just-made id.
      void videoSourceStore
        .flushChanges()
        .then(() => adapter.setActiveElements(id));
    },
    get isPlaying() {
      return adapter.isPlaying;
    },
    get canvasElement() {
      return rendererContainerRef.current;
    },
    get topLevelElements() {
      return videoSourceStore.source.elements;
    },
    isOriginalVideoElement: source => adapter.isOriginalVideoElement(source),
    editorStore,
    persist: () => {
      // The graphics-card chip's save in this modal (PLAT-10191).
      void adapter.saveStoryAndVideo(false, true, false, true);
    },
    textProducer,
    layout: {
      videoSourceStore,
      sceneLayoutIndexStore: editorStore.sceneLayoutIndexStore,
      get sceneLayoutSelectionManager() {
        return adapter.sceneLayoutSelectionManager;
      },
      get currentVideo() {
        return adapter.currentVideo;
      },
    },
    captions: {
      get textBrandingService() {
        return adapter.textBrandingService;
      },
      karaokeProducer,
      get time() {
        return adapter.time;
      },
      get aspectRatio() {
        return adapter.getCurrentAspectRatio();
      },
      get customFonts() {
        return adapter.organization?.branding?.customFonts || [];
      },
      sceneLayoutIndexStore: editorStore.sceneLayoutIndexStore,
      get originalVideoElementIdAtTime() {
        return (
          findOriginalVideoElementAtCurrentTime(
            adapter as unknown as IVideoCreatorStore,
          )?.id ?? undefined
        );
      },
      toggle: adapter,
      offerStyle: false,
    },
    layer: { zIndex: CLIP_PREVIEW_Z.canvasActionLayer },
    editText: quickEdit
      ? anchor => quickEdit.current?.openQuickEdit(anchor)
      : undefined,
  };
}

/** The clip preview's host, built once per adapter; `null` until it exists. */
export function useClipPreviewToolbarHost(
  input: Partial<ClipPreviewToolbarHostInput> &
    Pick<ClipPreviewToolbarHostInput, 'rendererContainerRef'>,
): FloatingToolbarHost | null {
  const {
    adapter,
    editorStore,
    videoSourceStore,
    rendererContainerRef,
    quickEdit,
  } = input;
  return useMemo(
    () =>
      adapter && editorStore && videoSourceStore
        ? createClipPreviewToolbarHost({
            adapter,
            editorStore,
            videoSourceStore,
            rendererContainerRef,
            quickEdit,
          })
        : null,
    [adapter, editorStore, videoSourceStore, rendererContainerRef, quickEdit],
  );
}
